import React, { useCallback, useEffect } from 'react';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { StyleSheet, View, ActivityIndicator, Platform } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import RootNavigator from './src/navigation/RootNavigator';
import { AuthProvider } from './src/contexts/AuthContext';
import { NotificationProvider } from './src/contexts/NotificationContext';
import { SidebarProvider } from './src/contexts/SidebarContext';
import { ThemeProvider, useTheme } from './src/contexts/ThemeContext';
import { LanguageProvider } from './src/contexts/LanguageContext';
import { YearProvider } from './src/contexts/YearContext';
import { navigationRef } from './src/services/navigationService';
import './src/i18n'; // Initialize i18n
import { COLORS } from './src/constants/config';
import { QueryProvider } from './src/providers/QueryProvider';
import linking from './src/navigation/linking';

// Inject global scrollbar styles on web
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    * { scrollbar-width: unset !important; scrollbar-color: unset !important; }
    *::-webkit-scrollbar { width: 8px !important; height: 8px !important; display: block !important; }
    *::-webkit-scrollbar-track { background: #f0f0f0 !important; border-radius: 4px !important; }
    *::-webkit-scrollbar-thumb { background-color: #bbb !important; border-radius: 4px !important; }
    *::-webkit-scrollbar-thumb:hover { background-color: #999 !important; }
  `;
  document.head.appendChild(style);
}

// Keep splash screen visible while loading fonts
SplashScreen.preventAutoHideAsync();

export default function App() {
  const [fontsLoaded] = useFonts({
    'JF-Flat-Regular': require('./assets/fonts/JF-Flat-Regular.ttf'),
    'JF-Flat-Medium': require('./assets/fonts/JF-Flat-Medium.ttf'),
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  if (!fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  return (
    <GestureHandlerRootView style={styles.container} onLayout={onLayoutRootView}>
      <SafeAreaProvider>
        <ThemeProvider>
          <LanguageProvider>
            <QueryProvider>
              <NotificationProvider>
                <AuthProvider>
                  <SidebarProvider>
                    <YearProvider>
                      <ThemedApp />
                    </YearProvider>
                  </SidebarProvider>
                </AuthProvider>
              </NotificationProvider>
            </QueryProvider>
          </LanguageProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

// Separate component to access theme context
function ThemedApp() {
  const { isDark } = useTheme();

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      fallback={
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.background }}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      }
      documentTitle={{
        formatter: () => 'نظام متابعة الأداء الاستراتيجي',
      }}
    >
      <RootNavigator />
      <StatusBar style={isDark ? 'light' : 'dark'} />
    </NavigationContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
  },
});
